import { useState } from "react";
import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@circle-react/contexts";
import type { RoomType } from "@circle-react/helpers/liveStreamRoomHelpers";
import { ROOM_TYPES } from "@circle-react/helpers/liveStreamRoomHelpers";
import { CustomizeUI } from "@circle-react-shared/CustomizeUI";
import { FullScreenModal } from "@circle-react-shared/ModalsV2/FullScreenModal";
import { Form } from "@circle-react-uikit/Form";
import { useToast } from "@circle-react-uikit/ToastV2";
import { UpgradePlan } from "../../UpgradePlan";
import { useAddonsContext } from "./AddonsProvider";
import { Body } from "./Common/Body";
import { Footer } from "./Common/Footer";
import { Header } from "./Common/Header";
import { useCreateLiveMutation } from "./hooks/useCreateLiveMutation";
import { useDefaultValues } from "./hooks/useDefaultValues";
import { useFormSections } from "./hooks/useFormSections";
import { useValidateFormSteps } from "./hooks/useValidateFormSteps";
import { SCROLLABLE_TARGET_ID } from "./utils/constants";

interface LiveFormV3Props {
  isOpen: boolean;
  onClose: () => void;
}

export const LiveFormV3 = ({ isOpen, onClose }: LiveFormV3Props) => {
  const { currentCommunity } = usePunditUserContext();
  const { supports_live_streams: hasLiveStreamsSupport } = currentCommunity || {
    supports_live_streams: false,
  };
  const [roomType, setRoomType] = useState<RoomType>(ROOM_TYPES.STREAM);
  const { sections, sectionTitles } = useFormSections(roomType);
  const defaultValues = useDefaultValues();
  const { mutateAsync, isLoading: isSaving } = useCreateLiveMutation(
    roomType,
    onClose,
  );

  const { validateAndContinue, triggerValidationRef } = useValidateFormSteps();

  const { success } = useToast();

  const { shouldUpgradeAddons, purchaseStreamingAddonWhenLimitReached } =
    useAddonsContext();

  const handleSubmit = async (params: Record<string, unknown>) => {
    try {
      await purchaseStreamingAddonWhenLimitReached();
      if (shouldUpgradeAddons)
        success(t("settings.billing.add_ons.changes_succeeded"));
      await mutateAsync(params);
    } catch (err: unknown) {
      // Toaster error already being displayed from mutation
      if (err instanceof Error) {
        console.error(err.message);
      }
    }
  };

  return (
    <FullScreenModal
      isOpen={isOpen}
      onClose={onClose}
      shouldShowCloseButton
      contentPortalId="live"
    >
      <CustomizeUI
        formId="live-QWZ7d7ysBkm5cwhL235J9Q"
        sections={sectionTitles}
        initialSection={sections.roomType}
        title={t("live_streams.form.title_v3")}
        onExit={onClose}
        onSave={handleSubmit}
        isSaving={isSaving}
      >
        <FullScreenModal.Header>
          <Header />
        </FullScreenModal.Header>
        <FullScreenModal.Body
          id={SCROLLABLE_TARGET_ID}
          wrapperClassName="max-w-full"
        >
          {hasLiveStreamsSupport && (
            <Form
              id="live-QWZ7d7ysBkm5cwhL235J9Q"
              onSubmit={handleSubmit}
              defaultValues={defaultValues}
            >
              <Body
                setRoomType={setRoomType}
                triggerValidationRef={triggerValidationRef}
              />
            </Form>
          )}
          {!hasLiveStreamsSupport && (
            <div className="mx-auto max-w-screen-md sm:py-14">
              <UpgradePlan />
            </div>
          )}
        </FullScreenModal.Body>
        {hasLiveStreamsSupport && (
          <FullScreenModal.Footer>
            <Footer
              roomType={roomType}
              validateAndContinue={validateAndContinue}
            />
          </FullScreenModal.Footer>
        )}
      </CustomizeUI>
    </FullScreenModal>
  );
};
